import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Tooltip, Checkbox } from 'antd';
import {
  MessageOutlined,
  CalendarOutlined,
  SendOutlined,
  FolderOutlined,
} from '@ant-design/icons';

// A referral status change can silently send an auto-SMS, fax an auto-reply,
// recompute the due date from default_timeline (blanking it when nil), and
// auto-archive. EMR appointment monitoring is org-level config
// that rarely varies per status, so it is intentionally omitted from these
// hints — surfacing it just adds noise. These helpers turn the remaining
// per-status config (now included in ReferralStatus#object) into user-facing
// hints so none of those side effects are a surprise.

export const willReplaceDueDate = (status, currentDueDate) => {
  if (!status || !currentDueDate) return false;
  const timeline = status.default_timeline;
  if (timeline === null || timeline === undefined) return true; // change blanks the date
  return !moment(currentDueDate).isSame(moment().add(timeline, 'days'), 'day');
};

export const buildReferralStatusSuccessMessage = ({
  linkedSmsTemplate,
  linkedReplyTemplate,
  patientPhoneNumber,
  statusChanged = false,
  previousDueDate,
  resultingDueDate,
  dueDatePreserved = false,
}) => {
  let successMessage = 'Referral status successfully updated';
  if (linkedSmsTemplate && patientPhoneNumber && linkedReplyTemplate) {
    successMessage += ' and SMS + referral reply message successfully sent!';
  } else if (linkedSmsTemplate && patientPhoneNumber) {
    successMessage += ' and SMS message successfully sent!';
  } else if (linkedReplyTemplate) {
    successMessage += ' and referral reply successfully sent!';
  }
  if (!statusChanged) return successMessage;

  if (dueDatePreserved && resultingDueDate) {
    successMessage += ` Kept your due date (${moment(resultingDueDate).format('MMM D')}).`;
  } else if (resultingDueDate) {
    successMessage += ` Due date set to ${moment(resultingDueDate).format('MMM D')}.`;
  } else if (previousDueDate) {
    successMessage += ' Due date cleared.';
  }

  return successMessage;
};

// Marks the status whose automatic reply is held (reply_held_status_id from
// the referred document status endpoint). Returns new objects, never mutating
// the shared status list.
export const markReplyHeldStatus = (statuses, replyHeldStatusId) => {
  if (!replyHeldStatusId) return statuses;
  return statuses.map((status) => (
    status.id === replyHeldStatusId ? { ...status, reply_held_for_approval: true } : status
  ));
};

export const getStatusConsequences = (
  status,
  { currentDueDate, patientPhoneNumber } = {},
) => {
  if (!status) return [];
  const consequences = [];

  if (status.has_linked_sms_template) {
    let label = 'Texts the patient automatically';
    if (patientPhoneNumber === null || patientPhoneNumber === '') {
      label = 'SMS template linked — you\'ll be asked for the patient\'s phone number';
    } else if (patientPhoneNumber === undefined) {
      label = 'Texts the patient automatically (when a phone number is on file)';
    }
    consequences.push({
      key: 'sms',
      icon: <MessageOutlined />,
      shortLabel: 'SMS',
      label,
      tone: 'warning',
    });
  }

  if (status.has_linked_reply_template) {
    // reply_held_for_approval comes from a stored hold: this status's
    // automatic reply is waiting on a reply fax number decision.
    consequences.push(status.reply_held_for_approval ? {
      key: 'reply',
      icon: <SendOutlined />,
      shortLabel: 'Reply held',
      label: 'Automated reply is on hold until someone approves the reply fax number',
      tone: 'warning',
    } : {
      key: 'reply',
      icon: <SendOutlined />,
      shortLabel: 'Reply',
      label: 'May fax an automated reply to the referring office',
      tone: 'info',
    });
  }

  const timeline = status.default_timeline;
  if (timeline !== null && timeline !== undefined) {
    const newDate = moment().add(timeline, 'days');
    let label = `Sets due date to ${newDate.format('MMM D')} (${timeline}-day default)`;
    const replaces = currentDueDate && !newDate.isSame(moment(currentDueDate), 'day');
    if (replaces) {
      label += ` — replaces ${moment(currentDueDate).format('MMM D')}`;
    }
    consequences.push({
      key: 'due_date',
      icon: <CalendarOutlined />,
      shortLabel: `${timeline}d`,
      label,
      tone: replaces ? 'warning' : 'info',
    });
  } else if (currentDueDate) {
    consequences.push({
      key: 'due_date',
      icon: <CalendarOutlined />,
      shortLabel: 'Clear due',
      label: `Clears the due date (currently ${moment(currentDueDate).format('MMM D')})`,
      tone: 'warning',
    });
  }

  if (status.auto_archive) {
    consequences.push({
      key: 'archive',
      icon: <FolderOutlined />,
      shortLabel: 'Archive',
      label: 'Archives the record automatically',
      tone: 'info',
    });
  }

  return consequences;
};

// Readable chip strip for dropdown options. Short labels are scannable at a
// glance; the tooltip still carries the full sentence for detail.
export const ReferralStatusConsequenceBadges = ({ status }) => {
  const consequences = getStatusConsequences(status);
  if (consequences.length === 0) return null;

  return (
    <Tooltip
      mouseEnterDelay={0.35}
      mouseLeaveDelay={0}
      title={(
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {consequences.map((item) => (
            <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      )}
    >
      <span
        className="referral-status-consequence-badges"
      >
        {consequences.map((item) => (
          <span
            key={item.key}
            className={`referral-status-consequence-hint referral-status-consequence-hint--${item.key}`}
          >
            <span
              aria-hidden="true"
              className="referral-status-consequence-hint__icon"
            >
              {item.icon}
            </span>
            <span>{item.shortLabel}</span>
          </span>
        ))}
      </span>
    </Tooltip>
  );
};

ReferralStatusConsequenceBadges.propTypes = {
  status: PropTypes.object,
};

ReferralStatusConsequenceBadges.defaultProps = {
  status: null,
};

// "When you set this status" panel for the Update Referral modal. Optionally
// renders a keep-my-due-date checkbox when the change would replace or clear
// a due date that's already on the record.
export const ReferralStatusConsequencePanel = ({
  status,
  currentDueDate,
  patientPhoneNumber,
  showKeepDueDate,
  keepDueDate,
  onKeepDueDateChange,
}) => {
  const consequences = getStatusConsequences(status, { currentDueDate, patientPhoneNumber });
  if (consequences.length === 0) return null;

  const dueDateAffected = showKeepDueDate && willReplaceDueDate(status, currentDueDate);

  return (
    <div
      style={{
        background: '#fafafa',
        border: '1px solid #f0f0f0',
        borderRadius: '6px',
        padding: '10px 12px',
        marginBottom: '16px',
        maxWidth: '400px',
      }}
    >
      <div style={{ fontWeight: 500, marginBottom: '6px', color: '#595959' }}>
        When you set this status:
      </div>
      {consequences.map((item) => (
        <div
          key={item.key}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            padding: '2px 0',
            color: item.tone === 'warning' ? '#ad6800' : '#595959',
          }}
        >
          <span style={{ marginTop: '2px' }}>{item.icon}</span>
          <span>{item.label}</span>
        </div>
      ))}
      {dueDateAffected && (
        <div style={{ marginTop: '6px' }}>
          <Checkbox
            checked={keepDueDate}
            onChange={(e) => onKeepDueDateChange(e.target.checked)}
          >
            {`Keep my current due date (${moment(currentDueDate).format('MMM D')})`}
          </Checkbox>
        </div>
      )}
    </div>
  );
};

ReferralStatusConsequencePanel.propTypes = {
  status: PropTypes.object,
  currentDueDate: PropTypes.string,
  patientPhoneNumber: PropTypes.string,
  showKeepDueDate: PropTypes.bool,
  keepDueDate: PropTypes.bool,
  onKeepDueDateChange: PropTypes.func,
};

ReferralStatusConsequencePanel.defaultProps = {
  status: null,
  currentDueDate: null,
  patientPhoneNumber: undefined,
  showKeepDueDate: false,
  keepDueDate: false,
  onKeepDueDateChange: () => {},
};
